/**
 * Shared Gantt-style timeline used by Tasks (timeline layout) and Milestones.
 * Rows carry a bar (start→end), an optional diamond (target), and dependency
 * arrows drawn between bars. Bars can be dragged to move or resized at the edges.
 */
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { addDays, daysBetween, todayISO } from "@/lib/tasks";

export type TimelineRow = {
  id: string;
  label: React.ReactNode;
  depth?: number;
  start?: string | null;
  end?: string | null;
  /** Draw a milestone diamond at this date instead of/in addition to the bar end. */
  diamond?: string | null;
  color: string;
  progress?: number | null;
  muted?: boolean;
  onClick?: () => void;
  /** Called after a drag with new dates (either may be unchanged). */
  onChange?: (start: string | null, end: string | null) => void;
  warn?: string;
};

export type TimelineEdge = { from: string; to: string };
export type Zoom = "week" | "month" | "quarter";
const PX: Record<Zoom, number> = { week: 36, month: 14, quarter: 5 };
const ROW_H = 36;
const LABEL_W = 260;

export function Timeline({ rows, edges = [], zoom, onZoom }: { rows: TimelineRow[]; edges?: TimelineEdge[]; zoom: Zoom; onZoom: (z: Zoom) => void }) {
  const { t, i18n } = useTranslation();
  const today = todayISO();
  const px = PX[zoom];
  const { rangeStart, days } = useMemo(() => {
    const dates = rows.flatMap((r) => [r.start, r.end, r.diamond]).filter((x): x is string => !!x);
    const min = dates.length ? dates.reduce((a, b) => (a < b ? a : b)) : today;
    const max = dates.length ? dates.reduce((a, b) => (a > b ? a : b)) : today;
    const lo = addDays(min < today ? min : today, zoom === "week" ? -7 : zoom === "month" ? -14 : -30);
    const hi = addDays(max > today ? max : today, zoom === "week" ? 21 : zoom === "month" ? 45 : 120);
    return { rangeStart: lo, days: Math.max(30, daysBetween(lo, hi)) };
  }, [rows, zoom, today]);
  const x = (iso: string) => daysBetween(rangeStart, iso) * px;
  const width = days * px;

  // Month header ticks.
  const months: { x: number; label: string }[] = [];
  for (let i = 0; i < days; i++) {
    const d = addDays(rangeStart, i);
    if (d.endsWith("-01") || i === 0) months.push({ x: i * px, label: new Date(d + "T00:00:00").toLocaleDateString(i18n.language, { month: "short", year: i === 0 || d.startsWith(`${new Date().getFullYear()}`) === false ? "numeric" : undefined }) });
  }

  const [drag, setDrag] = useState<{ id: string; mode: "move" | "start" | "end"; x0: number; start: string; end: string; cur: { start: string; end: string } } | null>(null);
  const scroller = useRef<HTMLDivElement>(null);

  const onDown = (e: React.MouseEvent, r: TimelineRow, mode: "move" | "start" | "end") => {
    if (!r.onChange || !r.start || !r.end) return;
    e.preventDefault();
    e.stopPropagation();
    setDrag({ id: r.id, mode, x0: e.clientX, start: r.start, end: r.end, cur: { start: r.start, end: r.end } });
  };
  const onMove = (e: React.MouseEvent) => {
    if (!drag) return;
    const delta = Math.round((e.clientX - drag.x0) / px);
    let { start, end } = drag;
    if (drag.mode === "move") {
      start = addDays(drag.start, delta);
      end = addDays(drag.end, delta);
    }
    if (drag.mode === "start") start = addDays(drag.start, delta) <= drag.end ? addDays(drag.start, delta) : drag.end;
    if (drag.mode === "end") end = addDays(drag.end, delta) >= drag.start ? addDays(drag.end, delta) : drag.start;
    setDrag({ ...drag, cur: { start, end } });
  };
  const onUp = () => {
    if (!drag) return;
    const r = rows.find((row) => row.id === drag.id);
    if (r && (drag.cur.start !== drag.start || drag.cur.end !== drag.end)) r.onChange?.(drag.cur.start, drag.cur.end);
    setDrag(null);
  };

  const pos = new Map<string, { x1: number; x2: number; y: number }>();
  rows.forEach((r, i) => {
    const s = drag?.id === r.id ? drag.cur.start : r.start;
    const e = drag?.id === r.id ? drag.cur.end : r.end;
    if (s && e) pos.set(r.id, { x1: x(s), x2: x(e) + px, y: i * ROW_H + ROW_H / 2 });
    else if (r.diamond) pos.set(r.id, { x1: x(r.diamond), x2: x(r.diamond) + px, y: i * ROW_H + ROW_H / 2 });
  });

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex items-center gap-1 border-b border-border px-3 py-1.5 text-[12px]">
        <span className="text-muted">{t("timeline.zoom")}</span>
        {(["week", "month", "quarter"] as Zoom[]).map((z) => (
          <button key={z} className={`rounded px-2 py-0.5 ${zoom === z ? "bg-active text-fg" : "text-muted hover:bg-hover"}`} onClick={() => onZoom(z)}>{t(`timeline.${z}`)}</button>
        ))}
        <button className="ml-auto btn btn-ghost h-6 text-[12px]" onClick={() => scroller.current?.scrollTo({ left: Math.max(0, x(today) - 200), behavior: "smooth" })}>{t("timeline.today")}</button>
      </div>
      <div className="flex flex-1 overflow-hidden">
        <div className="shrink-0 border-r border-border" style={{ width: LABEL_W }}>
          <div className="h-8 border-b border-border" />
          {rows.map((r) => (
            <div key={r.id} className={`flex items-center gap-1.5 truncate border-b border-border/60 px-3 text-[13px] ${r.muted ? "text-muted" : ""} ${r.onClick ? "cursor-pointer hover:bg-hover" : ""}`} style={{ height: ROW_H, paddingLeft: 12 + (r.depth ?? 0) * 16 }} onClick={r.onClick}>
              {r.label}
            </div>
          ))}
        </div>
        <div ref={scroller} className="relative flex-1 overflow-auto" onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}>
          <div className="relative" style={{ width, height: 32 + rows.length * ROW_H }}>
            <div className="sticky top-0 z-10 h-8 border-b border-border bg-bg/95 text-[11px] text-muted">
              {months.map((m, i) => (
                <span key={i} className="absolute top-2 border-l border-border pl-1.5" style={{ left: m.x }}>{m.label}</span>
              ))}
            </div>
            {rows.map((_, i) => <div key={i} className="absolute left-0 right-0 border-b border-border/40" style={{ top: 32 + (i + 1) * ROW_H - 1 }} />)}
            <div className="absolute bottom-0 top-8 w-px bg-accent/70" style={{ left: x(today) + px / 2 }} title={t("timeline.today")} />
            <svg className="pointer-events-none absolute left-0 top-8" width={width} height={rows.length * ROW_H}>
              <defs>
                <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0 0L10 5L0 10z" fill="var(--text-muted)" /></marker>
              </defs>
              {edges.map((e) => {
                const a = pos.get(e.from);
                const b = pos.get(e.to);
                if (!a || !b) return null;
                const sx = a.x2 + 2;
                const ex = b.x1 - 2;
                const mid = Math.max(sx + 12, ex - 12);
                const path = ex > sx + 20 ? `M${sx} ${a.y} H${(sx + ex) / 2} V${b.y} H${ex}` : `M${sx} ${a.y} H${sx + 12} V${(a.y + b.y) / 2} H${ex - 12} V${b.y} H${ex} M${mid} ${b.y}`;
                return <path key={`${e.from}-${e.to}`} d={path} fill="none" stroke="var(--text-muted)" strokeWidth="1.2" markerEnd="url(#arrow)" opacity="0.8" />;
              })}
            </svg>
            {rows.map((r, i) => {
              const p = pos.get(r.id);
              if (!p) return null;
              const top = 32 + i * ROW_H + 9;
              const w = Math.max(px, p.x2 - p.x1);
              const draggable = !!r.onChange && !!r.start && !!r.end;
              return (
                <div key={r.id} className="absolute" style={{ left: p.x1, top, width: w, height: 18 }}>
                  {r.start && r.end && (
                    <div className={`relative h-full rounded-md ${draggable ? "cursor-grab active:cursor-grabbing" : ""} ${r.muted ? "opacity-50" : ""}`} style={{ background: `color-mix(in srgb, ${r.color} 33.333%, transparent)`, border: `1px solid ${r.color}` }} onMouseDown={(e) => onDown(e, r, "move")} onClick={r.onClick} title={`${r.start} → ${r.end}`}>
                      {r.progress != null && <div className="absolute inset-y-0 left-0 rounded-l-md" style={{ width: `${Math.round(r.progress * 100)}%`, background: r.color }} />}
                      {draggable && <div className="absolute inset-y-0 left-0 w-1.5 cursor-ew-resize" onMouseDown={(e) => onDown(e, r, "start")} />}
                      {draggable && <div className="absolute inset-y-0 right-0 w-1.5 cursor-ew-resize" onMouseDown={(e) => onDown(e, r, "end")} />}
                    </div>
                  )}
                  {r.diamond && (
                    <div className="absolute -top-0.5 h-5 w-5 rotate-45 rounded-sm border-2" style={{ left: x(r.diamond) - p.x1 + px / 2 - 10, background: r.color, borderColor: "var(--bg)" }} title={r.diamond} onClick={r.onClick} />
                  )}
                  {r.warn && <span className="absolute -right-5 top-0 text-warning" title={r.warn}>⚠</span>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
